import { h } from '@omni-torn/ui';
import { BOT_FEATURES, GRANTABLE_LEVELS, isActive, levelsFor, OMNI_TORN_ACCESS, OMNI_TORN_DEFAULT, SETTINGS_SYNC_ACCESS, SETTINGS_SYNC_DEFAULT, WEB_SECTIONS, type AccessTable, type Grant, type GrantableLevel, type Level, type Subscription } from '@omni-torn/shared';
import { server } from '../session';
import { box } from '../ui';

/**
 * Owner → Tiers (owner's decision 12, 2026-11-01: "I need an owners section where I can decide which features are available
 * to which tiers … a faction tier version where one person can pay for a faction"). The levels are the tiers.
 *
 *  - Subscriptions and faction tiers: a player's own tier, or a faction's tier one player pays for, each until a date —
 *    entered by hand (GET/PUT /api/owner/tiers*, the signed-in owner only). Nothing takes payments.
 *  - Who gets what: the one features × tiers table (access.json) — the website's sections and the Discord bot's commands
 *    and parts here; the extension's features in the extension's Owner tab (the same table; it lists them by name).
 */

interface TiersAnswer {
  features: AccessTable;
  subscriptions: Subscription[];
  factionTiers: Grant[];
}

interface Row {
  id: string;
  name: string;
  group: string;
  defaults: readonly Level[];
}

const TIERS: readonly GrantableLevel[] = GRANTABLE_LEVELS;
const label = (l: string) => l.charAt(0).toUpperCase() + l.slice(1);
const day = (ms: number) => new Date(ms).toLocaleDateString();

/** The rows this page can name: Omni-Torn itself, settings sync, the website's sections, the Discord bot. */
function rows(): Row[] {
  const out: Row[] = [
    { id: OMNI_TORN_ACCESS, name: 'Omni-Torn itself — anything beyond signing in and offering their data (extension and website)', group: 'Everywhere', defaults: OMNI_TORN_DEFAULT },
    { id: SETTINGS_SYNC_ACCESS, name: 'Settings sync — the same settings on every device', group: 'Everywhere', defaults: SETTINGS_SYNC_DEFAULT },
  ];
  for (const s of WEB_SECTIONS) if (!s.always && s.access !== 'owner') out.push({ id: s.featureId, name: s.name, group: 'Website', defaults: levelsFor(s.access) });
  for (const f of BOT_FEATURES) out.push({ id: f.id, name: f.name, group: `Discord bot — ${f.group}`, defaults: levelsFor(f.access) });
  return out;
}

export function tiersBox(): HTMLElement {
  const body = h('div', { 'data-field': 'tiers' }, h('p', { class: 'muted' }, 'Loading…'));
  const load = () =>
    server<TiersAnswer>('GET', '/api/owner/tiers')
      .then((r) => draw(r))
      .catch((err: unknown) => body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message)));

  const draw = (r: TiersAnswer) => {
    let table: AccessTable = { ...r.features };
    let subs = [...r.subscriptions];
    let facs = [...r.factionTiers];

    // ---------------------------------------------------------------- subscriptions and faction tiers
    const paidStatus = h('span', { class: 'save-status', role: 'status', 'data-field': 'paid-status' });
    const savePaid = async () => {
      paidStatus.textContent = 'Saving…';
      try {
        const next = await server<TiersAnswer>('PUT', '/api/owner/tiers/paid', { subscriptions: subs, factionTiers: facs });
        subs = next.subscriptions;
        facs = next.factionTiers;
        paidStatus.textContent = 'Saved.';
        drawPaid();
      } catch (err) {
        paidStatus.textContent = (err as Error).message;
      }
    };
    const kind = h('select', { 'data-field': 'tier-kind', 'aria-label': 'Player or faction' }, h('option', { value: 'player' }, 'Player (their own tier)'), h('option', { value: 'faction' }, 'Faction (one player pays)')) as HTMLSelectElement;
    const idIn = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Player or faction id', maxlength: '12', 'data-field': 'tier-id', 'aria-label': 'Player or faction id' }) as HTMLInputElement;
    const tier = h('select', { 'data-field': 'tier-level', 'aria-label': 'Tier' }, ...TIERS.filter((l) => l !== 'basic').map((l) => h('option', { value: l }, label(l)))) as HTMLSelectElement;
    tier.value = 'beta';
    const until = h('input', { type: 'date', 'data-field': 'tier-until', 'aria-label': 'Until (the last day)' }) as HTMLInputElement;
    const paidBy = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Paid by (player id)', maxlength: '12', 'data-field': 'tier-paidby', 'aria-label': 'Paid by (player id)', hidden: true }) as HTMLInputElement;
    const note = h('input', { type: 'text', placeholder: 'Note (for you)', maxlength: '200', 'data-field': 'tier-note', 'aria-label': 'Note' }) as HTMLInputElement;
    kind.addEventListener('change', () => (paidBy.hidden = kind.value !== 'faction'));
    const add = h('button', { type: 'button', class: 'btn-small', 'data-action': 'add-tier' }, 'Add') as HTMLButtonElement;
    add.addEventListener('click', () => {
      const id = Number(idIn.value.trim());
      const end = until.value ? Date.parse(`${until.value}T23:59:59`) : NaN;
      if (!Number.isInteger(id) || id <= 0) return void (paidStatus.textContent = 'Enter the player’s or faction’s id (the number in its Torn link).');
      if (!Number.isFinite(end) || end <= Date.now()) return void (paidStatus.textContent = 'Pick the last day it runs (in the future).');
      const now = Date.now();
      if (kind.value === 'player') {
        subs = [...subs.filter((s) => s.playerId !== id), { playerId: id, level: tier.value as GrantableLevel, until: end, plan: 'by hand', ref: '', createdAt: now, ...(note.value.trim() ? { note: note.value.trim() } : {}) }];
      } else {
        const payer = Number(paidBy.value.trim());
        if (!Number.isInteger(payer) || payer <= 0) return void (paidStatus.textContent = 'Enter who pays for the faction (their player id).');
        facs = [...facs.filter((g) => g.target !== id), { id: `t${id}-${now.toString(36)}`, kind: 'faction', target: id, level: tier.value as GrantableLevel, until: end, note: note.value.trim(), createdAt: now, paidBy: payer }];
      }
      [idIn.value, note.value, paidBy.value] = ['', '', ''];
      void savePaid();
    });
    const paidList = h('ul', { class: 'plain-list srv-list', 'data-field': 'paid-list' });
    const drawPaid = () => {
      const now = Date.now();
      const item = (key: string, title: string, facts: string[], remove: () => void, active: boolean) => {
        const rm = h('button', { type: 'button', class: 'btn-small', 'data-action': 'remove-tier' }, 'Remove') as HTMLButtonElement;
        rm.addEventListener('click', remove);
        return h('li', { class: 'srv', 'data-tier': key }, h('div', { class: 'srv-head' }, h('strong', {}, title), h('span', { class: `badge${active ? ' on' : ''}` }, active ? 'Running' : 'Ended')), h('div', { class: 'muted small' }, facts.filter(Boolean).join(' · ')), h('div', { class: 'srv-actions' }, rm));
      };
      const items = [
        ...subs.map((s) => item(`player:${s.playerId}`, `Player ${s.playerId}`, [`${label(s.level)} until ${day(s.until)}`, s.plan !== 'by hand' ? `plan ${s.plan}` : '', s.note ?? ''], () => ((subs = subs.filter((x) => x !== s)), void savePaid()), isActive(s, now))),
        ...facs.map((g) => item(`faction:${g.target}`, `Faction ${g.target}`, [`${label(g.level)} until ${g.until ? day(g.until) : '—'}`, `paid by player ${g.paidBy}`, 'its members only while they’re in it', g.note], () => ((facs = facs.filter((x) => x !== g)), void savePaid()), isActive(g, now))),
      ];
      paidList.replaceChildren(...(items.length ? items : [h('li', { class: 'muted' }, 'None yet.')]));
    };
    drawPaid();

    // ---------------------------------------------------------------- the table
    const tableStatus = h('span', { class: 'save-status', role: 'status', 'data-field': 'table-status' });
    const tbody = h('tbody', {});
    const all = rows();
    const known = new Set(all.map((x) => x.id));
    const extensionChanged = () => Object.keys(table).filter((id) => !known.has(id)).length;
    const extLine = h('p', { class: 'muted small', 'data-field': 'extension-rows' });
    const drawTable = () => {
      const out: HTMLElement[] = [];
      let group = '';
      for (const f of all) {
        if (f.group !== group) {
          group = f.group;
          out.push(h('tr', { class: 'tier-group' }, h('th', { colspan: String(TIERS.length + 1) }, group)));
        }
        const allowed = table[f.id] ?? f.defaults;
        const cells = TIERS.map((l) => {
          const cb = h('input', { type: 'checkbox', 'data-level': l, 'aria-label': `${f.name}: ${label(l)}` }) as HTMLInputElement;
          cb.checked = allowed.includes(l);
          cb.addEventListener('change', () => {
            const now = TIERS.filter((x) => (x === l ? cb.checked : (table[f.id] ?? f.defaults).includes(x)));
            const byDefault = f.defaults.filter((x) => x !== 'owner');
            if (now.length === byDefault.length && now.every((x) => byDefault.includes(x))) delete table[f.id];
            else table[f.id] = now;
            tableStatus.textContent = 'Not saved yet.';
            drawTable();
          });
          return h('td', { class: 'c' }, cb);
        });
        out.push(h('tr', { 'data-feature': f.id }, h('td', {}, f.name, table[f.id] ? h('span', { class: 'muted small' }, ' (changed)') : null), ...cells));
      }
      tbody.replaceChildren(...out);
      const n = extensionChanged();
      extLine.textContent = `The extension’s own features (${n ? `${n} changed from their defaults` : 'all on their defaults'}) are rows of this same table: change them in the extension — Torn → Settings → Omni-Torn → Owner → Who gets what (it names them; the website and bot rows are there too). Saving here keeps them as they are.`;
    };
    drawTable();
    const save = h('button', { type: 'button', class: 'btn-small', 'data-action': 'save-tiers-table' }, 'Save who gets what') as HTMLButtonElement;
    save.addEventListener('click', async () => {
      tableStatus.textContent = 'Saving…';
      try {
        const next = await server<{ features: AccessTable }>('PUT', '/api/owner/tiers/access', { features: table });
        table = { ...next.features };
        tableStatus.textContent = 'Saved.';
        drawTable();
      } catch (err) {
        tableStatus.textContent = (err as Error).message;
      }
    });

    body.replaceChildren(
      h(
        'p',
        { class: 'notice', 'data-field': 'no-payments' },
        h('strong', {}, 'No payments yet. '),
        'Nothing here charges anyone — you enter tiers by hand. Before taking money for a tier, check Torn’s rules on paid tools (Torn’s API terms: charging, ads or real-money donations need webmaster@torn.com’s OK in writing) and every outside service’s commercial-use terms (weav3r.dev: no commercial use without its authorization; FF Scouter, TornStats, tornprobability, KAL, S&R).',
      ),
      h('h3', {}, 'Subscriptions and faction tiers'),
      h('p', { class: 'muted small' }, 'A player’s own tier, or a faction’s tier one player pays for (its members have it only while they’re in it), each until a day you pick. If both apply, the higher tier wins; a level you give one player under Levels (extension) beats both. Only you see these — nobody is ever told which tier anyone has.'),
      h('div', { class: 'srv-add tier-add' }, kind, idIn, tier, until, paidBy, note, add),
      paidList,
      paidStatus,
      h('h3', {}, 'Who gets what'),
      h('p', { class: 'muted small' }, 'Ticks show which tiers get each feature (you always get everything). The website’s sections follow “Omni-Torn itself” too. The Discord bot’s rows are checked for whoever uses the command — their own tier, or their faction’s — once Torn or their own key linked their Discord to their Torn account (', h('code', {}, '/account key'), ', also in a DM with the bot); then it counts in every server with the bot. They don’t need “Omni-Torn itself” (servers are approved on their own, below). “Runs by itself” rows use the faction’s tier. The bot only ever says “this needs a higher Omni-Torn tier”.'),
      h('div', { class: 'table-wrap' }, h('table', { class: 'tiers-table' }, h('thead', {}, h('tr', {}, h('th', {}, 'Feature'), ...TIERS.map((l) => h('th', { class: 'c' }, label(l))))), tbody)),
      extLine,
      h('div', { class: 'srv-actions' }, save, tableStatus),
    );
  };
  void load();
  return box('Tiers', body, { id: 'owner-tiers' });
}
